// app/lib/offline/offline-queue.client.ts
// Unified offline queue management for transaction sync
//
// Learning Note: This is the SINGLE source of truth for offline transaction queuing.
// Both React components and Service Worker use this same queue structure.
// Database: radiomisr_queue (unified name, clearer than previous names)

const DB_NAME = "radiomisr_queue"; // Unified database name
const DB_VERSION = 2; // Incremented for schema changes (added status, idempotency_key)
const STORE_NAME = "queue";

/**
 * Generate UUID (fallback for older browsers)
 * Learning Note: crypto.randomUUID() is available in modern browsers.
 * Fallback ensures compatibility with older browsers.
 */
function generateUUID(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Generate idempotency key for duplicate prevention
 * Format: deviceId_timestamp_transactionType
 *
 * Learning Note: Idempotency keys prevent duplicate transactions during offline sync.
 * If the same transaction is sent twice (e.g., network retry), the server recognizes
 * it's a duplicate and returns the cached result instead of creating a new transaction.
 */
function generateIdempotencyKey(deviceToken: string, type: "EARN" | "REDEEM_DIRECT" | "REDEEM_RETURN"): string {
  const timestamp = Date.now();
  // Use first 8 chars of device token + timestamp + type
  const deviceId = deviceToken.substring(0, 8);
  return `${deviceId}_${timestamp}_${type}`;
}

/**
 * Unified queue transaction interface
 *
 * Learning Note: Status field allows UI to show sync progress:
 * - pending: Transaction queued, waiting to sync
 * - syncing: Currently being synced to server
 * - synced: Successfully synced (can be cleaned up later)
 * - failed: Sync failed (will retry based on retry_count)
 */
export interface QueuedTransaction {
  id: string; // UUID (client-generated, primary key)
  idempotency_key: string; // Format: deviceId_timestamp_type (prevents duplicates)
  type: "EARN" | "REDEEM_DIRECT" | "REDEEM_RETURN"; // Transaction type
  redeem_mode?: "normal" | "return"; // For redeem transactions
  customer_barcode: string; // Customer identifier
  amount?: number; // Purchase amount (for earn)
  points?: number; // Points to redeem (for redeem)
  device_token: string; // Device authentication token
  cashier_name?: string; // Cashier name
  notes?: string; // Optional transaction notes
  created_at: number; // Timestamp when queued (milliseconds)
  status: "pending" | "syncing" | "synced" | "failed"; // Sync status
  retry_count: number; // Number of retry attempts
  last_error?: string; // Last error message (if failed)
  synced_at?: number; // Timestamp when successfully synced
}

/**
 * Open IndexedDB database
 *
 * Learning Note: IndexedDB is the browser's persistent storage API.
 * It works offline and persists data even after browser restart.
 * We use it to queue transactions when offline and sync when online.
 */
function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Delete old object store if it exists (migration from old schema)
      if (db.objectStoreNames.contains(STORE_NAME)) {
        db.deleteObjectStore(STORE_NAME);
      }

      // Create new object store with unified schema
      const store = db.createObjectStore(STORE_NAME, {
        keyPath: "id",
      });

      // Indexes for fast queries
      store.createIndex("status", "status", { unique: false });
      store.createIndex("synced", "status", { unique: false }); // For backward compatibility
      store.createIndex("queued_at", "created_at", { unique: false });
      store.createIndex("idempotency_key", "idempotency_key", { unique: false });
      store.createIndex("customer_barcode", "customer_barcode", { unique: false });
    };
  });
}

/**
 * Add transaction to offline queue
 *
 * Learning Note: This function is called when a transaction is created offline
 * or when online transaction fails. The transaction is queued locally and will
 * be synced automatically when the device comes back online.
 */
export async function queueTransaction(payload: {
  type: "EARN" | "REDEEM_DIRECT" | "REDEEM_RETURN";
  redeem_mode?: "normal" | "return";
  customer_barcode: string;
  amount?: number;
  points?: number;
  device_token: string;
  cashier_name?: string;
  notes?: string;
  idempotency_key?: string; // Optional - will be generated if not provided
}): Promise<string> {
  try {
    const db = await openDatabase();
    const tx = db.transaction([STORE_NAME], "readwrite");
    const store = tx.objectStore(STORE_NAME);

    const id = generateUUID();
    const idempotencyKey =
      payload.idempotency_key || generateIdempotencyKey(payload.device_token, payload.type);

    const queuedTransaction: QueuedTransaction = {
      id,
      idempotency_key: idempotencyKey,
      type: payload.type,
      redeem_mode: payload.redeem_mode,
      customer_barcode: payload.customer_barcode,
      amount: payload.amount,
      points: payload.points,
      device_token: payload.device_token,
      cashier_name: payload.cashier_name,
      notes: payload.notes,
      created_at: Date.now(),
      status: "pending",
      retry_count: 0,
    };

    await new Promise<void>((resolve, reject) => {
      const request = store.add(queuedTransaction);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    // Trigger background sync if available (Service Worker)
    if (
      typeof window !== "undefined" &&
      "serviceWorker" in navigator &&
      "sync" in ServiceWorkerRegistration.prototype
    ) {
      try {
        const registration = await navigator.serviceWorker.ready;
        // @ts-ignore - Background Sync API
        await registration.sync.register("sync-transactions");
      } catch (error) {
        console.warn("[Offline Queue] Failed to register background sync:", error);
        // Not critical - auto-sync will handle it
      }
    }

    return id;
  } catch (error) {
    console.error("[Offline Queue] Failed to queue transaction:", error);
    throw error;
  }
}

/**
 * Get all unsynced transactions (status: pending or failed)
 *
 * Learning Note: This is used by sync functions to get transactions that need syncing.
 * We filter for 'pending' and 'failed' status, excluding 'synced' transactions.
 */
export async function getUnsyncedTransactions(): Promise<QueuedTransaction[]> {
  try {
    const db = await openDatabase();
    const tx = db.transaction([STORE_NAME], "readonly");
    const store = tx.objectStore(STORE_NAME);
    const index = store.index("status");

    return new Promise((resolve, reject) => {
      // Get pending transactions
      const pendingRequest = index.getAll("pending");
      pendingRequest.onsuccess = () => {
        const pending = pendingRequest.result;

        // Get failed transactions (for retry)
        const failedRequest = index.getAll("failed");
        failedRequest.onsuccess = () => {
          const failed = failedRequest.result;
          resolve([...pending, ...failed]);
        };
        failedRequest.onerror = () => reject(failedRequest.error);
      };
      pendingRequest.onerror = () => reject(pendingRequest.error);
    });
  } catch (error) {
    console.error("[Offline Queue] Failed to get unsynced transactions:", error);
    return [];
  }
}

/**
 * Update transaction status
 *
 * Learning Note: Status updates allow UI to show sync progress and handle errors.
 * This is called during sync process to track progress.
 */
async function updateTransactionStatus(
  id: string,
  status: QueuedTransaction["status"],
  error?: string
): Promise<void> {
  try {
    const db = await openDatabase();
    const tx = db.transaction([STORE_NAME], "readwrite");
    const store = tx.objectStore(STORE_NAME);

    const getRequest = store.get(id);
    await new Promise<void>((resolve, reject) => {
      getRequest.onsuccess = () => {
        const transaction = getRequest.result;
        if (transaction) {
          transaction.status = status;
          if (status === "synced") {
            transaction.synced_at = Date.now();
          }
          if (error) {
            transaction.last_error = error;
            transaction.retry_count = (transaction.retry_count || 0) + 1;
          }
          const putRequest = store.put(transaction);
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          resolve(); // Transaction not found (already deleted?)
        }
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  } catch (error) {
    console.error("[Offline Queue] Failed to update transaction status:", error);
  }
}

/**
 * Mark transaction as synced
 *
 * Learning Note: This is called after successful sync to mark transaction as complete.
 * Synced transactions can be cleaned up later to free up storage.
 */
export async function markAsSynced(id: string): Promise<void> {
  await updateTransactionStatus(id, "synced");
}

/**
 * Mark transaction as syncing
 *
 * Learning Note: This is called at the start of sync to prevent duplicate sync attempts.
 */
export async function markAsSyncing(id: string): Promise<void> {
  await updateTransactionStatus(id, "syncing");
}

/**
 * Mark transaction as failed
 *
 * Learning Note: This is called when sync fails. The transaction will be retried later
 * based on retry_count (exponential backoff).
 */
export async function markAsFailed(id: string, error: string): Promise<void> {
  await updateTransactionStatus(id, "failed", error);
}

/**
 * Increment retry count for a transaction
 *
 * Learning Note: Retry count is used to implement exponential backoff.
 * After too many retries, we stop trying to prevent infinite loops.
 */
export async function incrementRetryCount(id: string): Promise<void> {
  try {
    const db = await openDatabase();
    const tx = db.transaction([STORE_NAME], "readwrite");
    const store = tx.objectStore(STORE_NAME);

    const getRequest = store.get(id);
    await new Promise<void>((resolve, reject) => {
      getRequest.onsuccess = () => {
        const transaction = getRequest.result;
        if (transaction) {
          transaction.retry_count = (transaction.retry_count || 0) + 1;
          const putRequest = store.put(transaction);
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          resolve();
        }
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  } catch (error) {
    console.error("[Offline Queue] Failed to increment retry count:", error);
  }
}

/**
 * Delete transaction from queue
 *
 * Learning Note: This is used to clean up synced transactions or remove failed
 * transactions that exceeded retry limit.
 */
export async function deleteTransaction(id: string): Promise<void> {
  try {
    const db = await openDatabase();
    const tx = db.transaction([STORE_NAME], "readwrite");
    const store = tx.objectStore(STORE_NAME);

    await new Promise<void>((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error("[Offline Queue] Failed to delete transaction:", error);
  }
}

/**
 * Sync all unsynced transactions
 * Returns number of successfully synced transactions
 *
 * Learning Note: This function is called automatically every 30 seconds when online,
 * or immediately when device comes back online. It processes pending transactions
 * and syncs them to the server.
 */
export async function syncQueue(): Promise<number> {
  if (typeof window === "undefined" || !navigator.onLine) {
    return 0;
  }

  const unsynced = await getUnsyncedTransactions();
  let syncedCount = 0;

  for (const transaction of unsynced) {
    try {
      // Skip if retry count is too high (prevent infinite retries)
      if (transaction.retry_count >= 10) {
        console.warn("[Offline Queue] Skipping transaction with too many retries:", transaction.id);
        await markAsFailed(transaction.id, "Max retries exceeded");
        continue;
      }

      // Mark as syncing to prevent duplicate sync attempts
      await markAsSyncing(transaction.id);

      // Determine endpoint based on transaction type
      const endpoint =
        transaction.type === "EARN" ? "/api/transactions/earn" : "/api/transactions/redeem-direct";

      // Build request body
      const body: any = {
        barcode: transaction.customer_barcode,
        idempotency_key: transaction.idempotency_key,
        cashier_name: transaction.cashier_name || "Unknown",
      };

      if (transaction.type === "EARN") {
        body.amount = transaction.amount;
      } else {
        body.points_to_redeem = transaction.points;
      }

      if (transaction.notes) {
        body.notes = transaction.notes;
      }

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Device-Token": transaction.device_token,
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        // Mark as synced
        await markAsSynced(transaction.id);
        syncedCount++;
      } else {
        const errorText = await response.text();
        await markAsFailed(
          transaction.id,
          `HTTP ${response.status}: ${errorText.substring(0, 100)}`
        );
      }
    } catch (error: any) {
      console.error("[Offline Queue] Failed to sync transaction:", error);
      await markAsFailed(transaction.id, `Network error: ${error.message || "Unknown error"}`);
    }
  }

  return syncedCount;
}

/**
 * Start automatic sync interval
 * Syncs every 30 seconds when online
 *
 * Learning Note: This sets up automatic background syncing. It syncs immediately
 * if online, then every 30 seconds. It also syncs when device comes back online.
 * Returns a cleanup function to stop syncing.
 */
export function startAutoSync(intervalMs: number = 30000): () => void {
  let intervalId: NodeJS.Timeout | null = null;

  const sync = async () => {
    if (navigator.onLine) {
      const count = await syncQueue();
      if (count > 0) {
        console.log(`[Offline Queue] Synced ${count} transaction(s)`);
      }
    }
  };

  // Sync immediately if online
  if (navigator.onLine) {
    sync();
  }

  // Set up interval
  intervalId = setInterval(sync, intervalMs);

  // Also sync when coming back online
  const handleOnline = () => {
    sync();
  };
  window.addEventListener("online", handleOnline);

  // Return cleanup function
  return () => {
    if (intervalId) {
      clearInterval(intervalId);
    }
    window.removeEventListener("online", handleOnline);
  };
}

/**
 * Get queue statistics
 *
 * Learning Note: This provides observability into queue state. Useful for
 * debugging and showing users how many transactions are pending sync.
 */
export async function getQueueStats(): Promise<{
  total: number;
  pending: number;
  syncing: number;
  synced: number;
  failed: number;
}> {
  try {
    const db = await openDatabase();
    const tx = db.transaction([STORE_NAME], "readonly");
    const store = tx.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => {
        const transactions = request.result;
        resolve({
          total: transactions.length,
          pending: transactions.filter((t) => t.status === "pending").length,
          syncing: transactions.filter((t) => t.status === "syncing").length,
          synced: transactions.filter((t) => t.status === "synced").length,
          failed: transactions.filter((t) => t.status === "failed").length,
        });
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error("[Offline Queue] Failed to get queue stats:", error);
    return { total: 0, pending: 0, syncing: 0, synced: 0, failed: 0 };
  }
}

/**
 * Get transaction by ID
 *
 * Learning Note: Useful for checking status of specific transaction.
 */
export async function getTransaction(id: string): Promise<QueuedTransaction | null> {
  try {
    const db = await openDatabase();
    const tx = db.transaction([STORE_NAME], "readonly");
    const store = tx.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error("[Offline Queue] Failed to get transaction:", error);
    return null;
  }
}
